import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiUser, FiCalendar, FiClock, FiMapPin, FiPackage } = FiIcons;

const STATUS_COLORS = {
  pending_assignment: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  assigned: 'bg-blue-100 text-blue-800 border-blue-200',
  pickup_started: 'bg-purple-100 text-purple-800 border-purple-200',
  processing: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  ready_for_delivery: 'bg-orange-100 text-orange-800 border-orange-200',
  out_for_delivery: 'bg-teal-100 text-teal-800 border-teal-200',
  delivered: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
};

const formatStatus = (s) =>
  s?.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [pickupDriver, setPickupDriver] = useState('');
  const [deliveryDriver, setDeliveryDriver] = useState('');
  const [bookingServices, setBookingServices] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchBookings = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('bookings')
      .select(`*, profiles:customer_id(full_name, phone)`)
      .order('created_at', { ascending: false });
    if (data) setBookings(data);
    setLoading(false);
  };

  const fetchDrivers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, phone')
      .eq('role', 'driver');
    if (data) setDrivers(data);
  };

  useEffect(() => {
    fetchBookings();
    fetchDrivers();
  }, []);

  const openDetail = async (booking) => {
    setSelected(booking);
    setPickupDriver('');
    setDeliveryDriver('');
    // Fetch existing assignments
    const { data: assigns } = await supabase
      .from('driver_assignments')
      .select('*')
      .eq('booking_id', booking.id);
    if (assigns) {
      const pickup = assigns.find((a) => a.task_type === 'pickup');
      const delivery = assigns.find((a) => a.task_type === 'delivery');
      if (pickup) setPickupDriver(pickup.driver_id);
      if (delivery) setDeliveryDriver(delivery.driver_id);
    }
    // Fetch booking services
    const { data: services } = await supabase
      .from('booking_services')
      .select(`*, services(name, base_price)`)
      .eq('booking_id', booking.id);
    if (services) setBookingServices(services);
  };

  const handleAssign = async () => {
    if (!selected) return;
    setAssigning(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const upserts = [];
      if (pickupDriver) {
        upserts.push({
          booking_id: selected.id,
          driver_id: pickupDriver,
          assigned_by: user.id,
          task_type: 'pickup',
        });
      }
      if (deliveryDriver) {
        upserts.push({
          booking_id: selected.id,
          driver_id: deliveryDriver,
          assigned_by: user.id,
          task_type: 'delivery',
        });
      }
      if (upserts.length > 0) {
        const { error } = await supabase
          .from('driver_assignments')
          .upsert(upserts, { onConflict: 'booking_id,task_type' });
        if (error) throw error;
      }
      // Update booking status to assigned
      await supabase
        .from('bookings')
        .update({ status: 'assigned' })
        .eq('id', selected.id);

      await fetchBookings();
      setSelected(null);
    } catch (err) {
      alert('Assignment failed: ' + err.message);
    } finally {
      setAssigning(false);
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    await supabase.from('bookings').update({ status: newStatus }).eq('id', bookingId);
    await fetchBookings();
    if (selected?.id === bookingId) setSelected((p) => ({ ...p, status: newStatus }));
  };

  const filtered = filterStatus === 'all'
    ? bookings
    : bookings.filter((b) => b.status === filterStatus);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-brand-navy">All Bookings</h1>
        <span className="text-sm text-gray-500">{bookings.length} total</span>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {['all', 'pending_assignment', 'assigned', 'processing', 'delivered'].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              filterStatus === s
                ? 'bg-brand-navy text-white border-brand-navy'
                : 'bg-white text-gray-600 border-gray-200 hover:border-brand-navy'
            }`}
          >
            {s === 'all' ? 'All' : formatStatus(s)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-gray-200 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
          <SafeIcon name="Inbox" className="text-4xl text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No bookings found.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((booking) => (
            <Card
              key={booking.id}
              onClick={() => openDetail(booking)}
              className="cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-xs text-gray-400">{booking.booking_number}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLORS[booking.status]}`}>
                      {formatStatus(booking.status)}
                    </span>
                  </div>
                  <p className="font-bold text-brand-navy">{booking.profiles?.full_name || 'Customer'}</p>
                  <div className="flex gap-4 mt-1 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <SafeIcon name="Calendar" className="text-brand-gold" />
                      {booking.pickup_date}
                    </span>
                    <span className="flex items-center gap-1">
                      <SafeIcon name="Clock" className="text-brand-gold" />
                      {booking.pickup_slot?.split(' - ')[0]}
                    </span>
                  </div>
                </div>
                <div className="text-right ml-4">
                  <p className="font-bold text-brand-navy">₹{booking.total_amount || '—'}</p>
                  <p className="text-xs text-gray-400">{booking.payment_status}</p>
                  {booking.status === 'pending_assignment' && (
                    <span className="text-xs text-yellow-600 font-semibold">Needs Driver</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selected && (
        <BookingModal
          booking={selected}
          drivers={drivers}
          bookingServices={bookingServices}
          pickupDriver={pickupDriver}
          deliveryDriver={deliveryDriver}
          setPickupDriver={setPickupDriver}
          setDeliveryDriver={setDeliveryDriver}
          onAssign={handleAssign}
          onStatusChange={handleStatusChange}
          onClose={() => setSelected(null)}
          assigning={assigning}
        />
      )}
    </div>
  );
}

function BookingModal({
  booking, drivers, bookingServices,
  pickupDriver, deliveryDriver,
  setPickupDriver, setDeliveryDriver,
  onAssign, onStatusChange, onClose, assigning,
}) {
  const STATUSES = [
    'pending_assignment', 'assigned', 'pickup_started',
    'processing', 'ready_for_delivery', 'out_for_delivery', 'delivered', 'cancelled',
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="font-bold text-brand-navy text-lg">{booking.booking_number}</h2>
            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${STATUS_COLORS[booking.status]}`}>
              {booking.status?.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <FiX className="text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer Info */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="h-10 w-10 bg-brand-navy text-brand-gold rounded-full flex items-center justify-center font-bold">
              {booking.profiles?.full_name?.charAt(0) || 'C'}
            </div>
            <div>
              <p className="font-semibold">{booking.profiles?.full_name}</p>
              <p className="text-sm text-gray-500">{booking.profiles?.phone || 'No phone'}</p>
            </div>
          </div>

          {/* Schedule & Address */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-blue-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <FiCalendar className="text-brand-gold" /> Pickup Date
              </p>
              <p className="font-semibold text-sm">{booking.pickup_date}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-xl">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <FiClock className="text-brand-gold" /> Time Slot
              </p>
              <p className="font-semibold text-sm">{booking.pickup_slot}</p>
            </div>
          </div>

          <div className="p-3 bg-gray-50 rounded-xl">
            <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <FiMapPin className="text-brand-gold" /> Pickup Address
            </p>
            <p className="text-sm font-medium">
              {booking.pickup_address?.line1}, {booking.pickup_address?.locality}, {booking.pickup_address?.city}
              {booking.pickup_address?.landmark ? ` (Near ${booking.pickup_address.landmark})` : ''}
            </p>
          </div>

          {/* Services */}
          {bookingServices.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                <FiPackage className="text-brand-gold" /> Services Requested
              </p>
              <div className="space-y-1">
                {bookingServices.map((bs) => (
                  <div key={bs.id} className="flex justify-between text-sm py-1 border-b border-gray-50">
                    <span>{bs.quantity}x {bs.services?.name}</span>
                    <span className="text-gray-500">₹{(bs.services?.base_price * bs.quantity).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Driver Assignment */}
          <div className="border border-brand-gold/30 bg-brand-gold/5 rounded-xl p-4 space-y-3">
            <p className="font-semibold text-brand-navy flex items-center gap-2">
              <FiUser className="text-brand-gold" /> Assign Drivers
            </p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Pickup Driver</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold"
                value={pickupDriver}
                onChange={(e) => setPickupDriver(e.target.value)}
              >
                <option value="">— Select Driver —</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.full_name} {d.phone ? `(${d.phone})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Delivery Driver</label>
              <select
                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold"
                value={deliveryDriver}
                onChange={(e) => setDeliveryDriver(e.target.value)}
              >
                <option value="">— Select Driver —</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>{d.full_name} {d.phone ? `(${d.phone})` : ''}</option>
                ))}
              </select>
            </div>
            <Button
              onClick={onAssign}
              isLoading={assigning}
              disabled={!pickupDriver && !deliveryDriver}
              className="w-full"
              variant="accent"
            >
              {assigning ? 'Assigning...' : 'Save Assignment'}
            </Button>
          </div>

          {/* Status Update */}
          <div>
            <p className="text-xs text-gray-500 mb-2">Update Booking Status</p>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => onStatusChange(booking.id, s)}
                  className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${
                    booking.status === s
                      ? STATUS_COLORS[s] + ' font-bold'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-brand-navy'
                  }`}
                >
                  {s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}